import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { Button } from '../components/ui/button';
import { 
  Shield, 
  Check, 
  X,
  ArrowRight,
  HelpCircle
} from 'lucide-react';

const plans = [
  {
    name: 'Starter',
    price: 299,
    description: 'Perfect for small teams starting their compliance journey',
    popular: false,
    cta: 'Start Free Trial',
    features: {
      frameworks: '1 framework',
      users: '5 users',
      controls: 'Up to 100 controls',
      evidence: 'Manual evidence upload',
      storage: '5 GB storage',
      reports: 'Basic PDF reports',
      support: 'Email support',
      integrations: false,
      ai: false,
      bulkImport: false,
      customFrameworks: false,
      auditPortal: false,
      sso: false,
      api: false,
      dedicatedCSM: false,
      sla: false,
      vulnManagement: false,
      ctem: false
    }
  },
  {
    name: 'Professional',
    price: 799,
    description: 'For growing companies with multiple compliance needs',
    popular: true,
    cta: 'Start Free Trial',
    features: {
      frameworks: '4 frameworks',
      users: '20 users',
      controls: 'Up to 500 controls',
      evidence: 'Automated evidence collection',
      storage: '50 GB storage',
      reports: 'Advanced PDF reports',
      support: 'Priority email & chat',
      integrations: true,
      ai: true,
      bulkImport: true,
      customFrameworks: true,
      auditPortal: true,
      sso: false,
      api: false,
      dedicatedCSM: false,
      sla: false,
      vulnManagement: true,
      ctem: true
    }
  },
  {
    name: 'Enterprise',
    price: 1999,
    description: 'For large organizations with advanced security requirements',
    popular: false,
    cta: 'Contact Sales',
    features: {
      frameworks: 'Unlimited frameworks',
      users: 'Unlimited users',
      controls: 'Unlimited controls',
      evidence: 'Automated + custom scrapers',
      storage: 'Unlimited storage',
      reports: 'Custom branded reports',
      support: '24/7 phone & chat',
      integrations: true,
      ai: true,
      bulkImport: true,
      customFrameworks: true,
      auditPortal: true,
      sso: true,
      api: true,
      dedicatedCSM: true,
      sla: true,
      vulnManagement: true,
      ctem: true
    }
  }
];

const featureList = [
  { key: 'frameworks', label: 'Compliance Frameworks', tooltip: 'Number of frameworks you can use (SOC 2, ISO 27001, etc.)' },
  { key: 'users', label: 'Team Members', tooltip: 'Number of users who can access the platform' },
  { key: 'controls', label: 'Controls', tooltip: 'Maximum number of controls you can track' },
  { key: 'evidence', label: 'Evidence Collection', tooltip: 'How evidence is gathered for compliance' },
  { key: 'storage', label: 'Storage', tooltip: 'Space for storing evidence files and documents' },
  { key: 'reports', label: 'Compliance Reports', tooltip: 'PDF reports for audits and stakeholders' },
  { key: 'support', label: 'Support', tooltip: 'Level of customer support included' },
  { key: 'integrations', label: 'AWS, GitHub, Okta Integrations', tooltip: 'Automated evidence collection from cloud services' },
  { key: 'ai', label: 'AI Risk Assessment', tooltip: 'AI-powered risk scoring and recommendations' },
  { key: 'bulkImport', label: 'Bulk Evidence Import', tooltip: 'Import multiple evidence items via CSV' },
  { key: 'customFrameworks', label: 'Custom Frameworks', tooltip: 'Create your own compliance frameworks' },
  { key: 'auditPortal', label: 'Auditor Portal', tooltip: 'Read-only access for external auditors' },
  { key: 'sso', label: 'SAML SSO', tooltip: 'Single Sign-On with Okta, Azure AD, etc.' },
  { key: 'api', label: 'API Access', tooltip: 'RESTful API for custom integrations' },
  { key: 'dedicatedCSM', label: 'Dedicated Success Manager', tooltip: 'Personal point of contact for your account' },
  { key: 'sla', label: '99.9% Uptime SLA', tooltip: 'Service level agreement with uptime guarantee' },
  { key: 'vulnManagement', label: 'Vulnerability Management', tooltip: 'Enterprise vulnerability scanning with NVD/MITRE feeds and SLA-based remediation' },
  { key: 'ctem', label: 'CTEM Module', tooltip: 'Continuous Threat Exposure Management — 5-phase lifecycle for discovering, prioritizing, and remediating threats' }
];

const faqs = [
  {
    question: 'What happens after my 14-day trial?',
    answer: 'After your trial ends, you can choose a plan that fits your needs. Your data will be preserved, and you can upgrade or downgrade at any time. No credit card is required to start.'
  },
  {
    question: 'Can I change plans later?',
    answer: 'Yes, you can upgrade or downgrade your plan at any time. When upgrading, you get immediate access to new features. When downgrading, changes take effect at the next billing cycle.'
  },
  {
    question: 'What compliance frameworks are included?',
    answer: 'We support SOC 2, ISO 27001, GDPR, HIPAA, PCI-DSS, NIST 800-53, and FedRAMP out of the box. Enterprise customers can also create custom frameworks.'
  },
  {
    question: 'Is my data secure?',
    answer: 'Absolutely. We use AES-256 encryption at rest, TLS 1.3 in transit, and are SOC 2 Type II certified ourselves. Your compliance data is hosted in secure, isolated environments.'
  },
  {
    question: 'Do you offer discounts for annual billing?',
    answer: 'Yes, annual billing saves you 20% compared to monthly billing. Contact sales for custom enterprise pricing.'
  }
];

export default function PricingPage() {
  const [billingCycle, setBillingCycle] = useState('monthly');
  const [openFaq, setOpenFaq] = useState(null);

  const getPrice = (price) => {
    if (billingCycle === 'annual') {
      return Math.round(price * 0.8);
    }
    return price;
  };

  return (
    <div className="min-h-screen bg-white">
      {/* Navigation */}
      <nav className="fixed top-0 left-0 right-0 z-50 bg-white/90 backdrop-blur-sm border-b border-slate-200">
        <div className="max-w-7xl mx-auto px-6 py-4 flex items-center justify-between">
          <Link to="/" className="flex items-center gap-2">
            <div className="w-10 h-10 bg-slate-900 rounded flex items-center justify-center">
              <Shield className="w-6 h-6 text-white" />
            </div>
            <span className="text-xl font-bold text-slate-900">Zentry</span>
          </Link>
          <div className="flex items-center gap-4">
            <Link to="/login">
              <Button variant="ghost" className="text-slate-600">Sign In</Button>
            </Link>
            <Link to="/register">
              <Button className="bg-slate-900 hover:bg-slate-800">Start Free Trial</Button>
            </Link>
          </div>
        </div>
      </nav>

      {/* Header */}
      <section className="pt-32 pb-16 px-6">
        <div className="max-w-4xl mx-auto text-center">
          <h1 className="text-4xl md:text-5xl font-bold text-slate-900 mb-4">
            Simple, Transparent Pricing
          </h1>
          <p className="text-xl text-slate-600 mb-8">
            Choose the plan that fits your compliance needs. All plans include a 14-day free trial.
          </p>
          
          {/* Billing Toggle */}
          <div className="inline-flex items-center gap-4 p-1 bg-slate-100 rounded-lg">
            <button
              onClick={() => setBillingCycle('monthly')}
              className={`px-4 py-2 rounded-md text-sm font-medium transition ${
                billingCycle === 'monthly' 
                  ? 'bg-white text-slate-900 shadow-sm' 
                  : 'text-slate-600'
              }`}
            >
              Monthly
            </button>
            <button
              onClick={() => setBillingCycle('annual')}
              className={`px-4 py-2 rounded-md text-sm font-medium transition ${
                billingCycle === 'annual' 
                  ? 'bg-white text-slate-900 shadow-sm' 
                  : 'text-slate-600'
              }`}
            >
              Annual <span className="text-green-600 ml-1">Save 20%</span>
            </button>
          </div>
        </div>
      </section>

      {/* Pricing Cards */}
      <section className="pb-16 px-6">
        <div className="max-w-6xl mx-auto">
          <div className="grid md:grid-cols-3 gap-8">
            {plans.map((plan, index) => (
              <div 
                key={index} 
                className={`relative rounded-2xl border-2 p-8 ${
                  plan.popular 
                    ? 'border-blue-600 shadow-xl' 
                    : 'border-slate-200'
                }`}
              >
                {plan.popular && (
                  <div className="absolute -top-4 left-1/2 -translate-x-1/2 px-4 py-1 bg-blue-600 text-white text-sm font-medium rounded-full">
                    Most Popular
                  </div>
                )}
                <div className="text-center mb-8">
                  <h3 className="text-2xl font-bold text-slate-900 mb-2">{plan.name}</h3>
                  <p className="text-slate-600 text-sm mb-4">{plan.description}</p>
                  <div className="flex items-baseline justify-center gap-1">
                    <span className="text-5xl font-bold text-slate-900">${getPrice(plan.price)}</span>
                    <span className="text-slate-600">/month</span>
                  </div>
                  {billingCycle === 'annual' && (
                    <p className="text-sm text-green-600 mt-2">
                      ${getPrice(plan.price) * 12}/year (billed annually)
                    </p>
                  )}
                </div>
                
                <Link to="/register" className="block mb-8">
                  <Button 
                    className={`w-full ${
                      plan.popular 
                        ? 'bg-blue-600 hover:bg-blue-700' 
                        : 'bg-slate-900 hover:bg-slate-800'
                    }`}
                    size="lg"
                  >
                    {plan.cta}
                    <ArrowRight className="ml-2 w-4 h-4" />
                  </Button>
                </Link>

                <div className="space-y-4">
                  <div className="text-sm font-semibold text-slate-900 uppercase tracking-wide">
                    What&apos;s included
                  </div>
                  {featureList.slice(0, 7).map((feature, fIndex) => (
                    <div key={fIndex} className="flex items-start gap-3">
                      <Check className="w-5 h-5 text-green-600 flex-shrink-0 mt-0.5" />
                      <span className="text-slate-700 text-sm">
                        {typeof plan.features[feature.key] === 'string' 
                          ? plan.features[feature.key]
                          : feature.label}
                      </span>
                    </div>
                  ))}
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Feature Comparison Table */}
      <section className="py-16 px-6 bg-slate-50">
        <div className="max-w-6xl mx-auto">
          <h2 className="text-3xl font-bold text-slate-900 text-center mb-12">
            Compare All Features
          </h2>
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead>
                <tr className="border-b border-slate-200">
                  <th className="text-left py-4 px-4 font-medium text-slate-600">Feature</th>
                  {plans.map((plan, index) => (
                    <th key={index} className="text-center py-4 px-4 font-semibold text-slate-900">
                      {plan.name}
                    </th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {featureList.map((feature, index) => (
                  <tr key={index} className="border-b border-slate-100">
                    <td className="py-4 px-4">
                      <div className="flex items-center gap-2">
                        <span className="text-slate-700">{feature.label}</span>
                        <div className="group relative">
                          <HelpCircle className="w-4 h-4 text-slate-400 cursor-help" />
                          <div className="absolute left-0 bottom-full mb-2 hidden group-hover:block w-48 p-2 bg-slate-900 text-white text-xs rounded-lg z-10">
                            {feature.tooltip}
                          </div>
                        </div>
                      </div>
                    </td>
                    {plans.map((plan, pIndex) => (
                      <td key={pIndex} className="text-center py-4 px-4">
                        {typeof plan.features[feature.key] === 'string' ? (
                          <span className="text-slate-700 text-sm">{plan.features[feature.key]}</span>
                        ) : plan.features[feature.key] ? (
                          <Check className="w-5 h-5 text-green-600 mx-auto" />
                        ) : (
                          <X className="w-5 h-5 text-slate-300 mx-auto" />
                        )}
                      </td>
                    ))}
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      </section>

      {/* FAQ Section */}
      <section className="py-16 px-6">
        <div className="max-w-3xl mx-auto">
          <h2 className="text-3xl font-bold text-slate-900 text-center mb-12">
            Frequently Asked Questions
          </h2>
          <div className="space-y-4">
            {faqs.map((faq, index) => (
              <div 
                key={index} 
                className="border border-slate-200 rounded-lg overflow-hidden"
              >
                <button
                  onClick={() => setOpenFaq(openFaq === index ? null : index)}
                  className="w-full px-6 py-4 text-left flex items-center justify-between bg-white hover:bg-slate-50 transition"
                >
                  <span className="font-medium text-slate-900">{faq.question}</span>
                  <span className="text-2xl text-slate-400">
                    {openFaq === index ? '−' : '+'}
                  </span>
                </button>
                {openFaq === index && (
                  <div className="px-6 py-4 bg-slate-50 border-t border-slate-200">
                    <p className="text-slate-600">{faq.answer}</p>
                  </div>
                )}
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-16 px-6 bg-slate-900">
        <div className="max-w-4xl mx-auto text-center">
          <h2 className="text-3xl font-bold text-white mb-4">
            Ready to Get Started?
          </h2>
          <p className="text-slate-300 mb-8">
            Start your 14-day free trial today. No credit card required.
          </p>
          <Link to="/register">
            <Button size="lg" className="bg-white text-slate-900 hover:bg-slate-100">
              Start Free Trial
              <ArrowRight className="ml-2 w-5 h-5" />
            </Button>
          </Link>
        </div>
      </section>

      {/* Footer */}
      <footer className="py-8 px-6 bg-slate-50 border-t border-slate-200">
        <div className="max-w-7xl mx-auto text-center text-sm text-slate-600">
          © 2026 Zentry. All rights reserved.
        </div>
      </footer>
    </div>
  );
}
